import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { BookOpen, Plus, Pencil, Trash2, X, Calendar, AlertTriangle, ClipboardList } from 'lucide-react';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import ConfigurarPruebaModal from '../../components/ConfigurarPruebaModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const toTitleCase = (s) => {
  if (!s) return '';
  return s.toLowerCase().replace(/(?:^|\s|[-/])\S/g, c => c.toUpperCase());
};

export default function CapacitadorCursos() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [capacitaciones, setCapacitaciones] = useState([]);
  const [cursoPrueba, setCursoPrueba] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 7;
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [form, setForm] = useState({
    nombre: '', descripcion: '', vigencia_meses: 12, obligatoria: true,
    es_transversal: true, horas: '', modalidad: 'e-learning',
    porcentaje_aprobacion: 70, calendarizable: true, area_id: null,
    actividad_id: null, video_url: '', pdf_url: '', imagen_url: ''
  });

  const fetchCapacitaciones = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/capacitaciones`, { headers });
      setCapacitaciones((res.data || []).filter(c => c.calendarizable));
    } catch { }
  }, [token]);

  useEffect(() => { fetchCapacitaciones(); }, [fetchCapacitaciones]);

  const resetForm = () => {
    setForm({ nombre: '', descripcion: '', vigencia_meses: 12, obligatoria: true,
      es_transversal: true, horas: '', modalidad: 'e-learning',
      porcentaje_aprobacion: 70, calendarizable: true, area_id: null,
      actividad_id: null, video_url: '', pdf_url: '', imagen_url: '' });
    setEditing(null);
  };

  const openEdit = (cap) => {
    setForm({
      nombre: cap.nombre, descripcion: cap.descripcion || '',
      vigencia_meses: cap.vigencia_meses, obligatoria: cap.obligatoria,
      es_transversal: cap.es_transversal || false, horas: cap.horas || '',
      modalidad: cap.modalidad || 'e-learning', calendarizable: cap.calendarizable || false,
      porcentaje_aprobacion: cap.porcentaje_aprobacion || 70,
      area_id: cap.area_id, actividad_id: cap.actividad_id,
      video_url: cap.video_url || '', pdf_url: cap.pdf_url || '', imagen_url: cap.imagen_url || ''
    });
    setEditing(cap.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API}/capacitaciones/${editing}`, form, { headers });
        toast.success('Capacitacion actualizada');
      } else {
        await axios.post(`${API}/capacitaciones`, form, { headers });
        toast.success('Capacitacion creada');
      }
      setShowForm(false);
      resetForm();
      fetchCapacitaciones();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleDelete = (id, nombre) => {
    setConfirmAction({
      message: `¿Eliminar "${nombre}"? Esta accion no se puede deshacer.`,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/capacitaciones/${id}`, { headers });
          toast.success('Capacitacion eliminada');
          fetchCapacitaciones();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
        setConfirmAction(null);
      }
    });
  };

  return (
    <div className="space-y-6" data-testid="capacitador-cursos">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Capacitaciones</h1>
        <button onClick={() => { resetForm(); setShowForm(true); }}
          className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium">
          <Plus className="w-4 h-4" />Nueva Capacitacion
        </button>
      </div>

      <div className="grid gap-3">
        {capacitaciones.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map(cap => (
          <div key={cap.id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between hover:border-teal-200 transition-colors">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center shrink-0">
                <BookOpen className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <h3 className="font-medium text-slate-800">{cap.nombre}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-slate-400">{cap.modalidad} | {cap.horas || '?'}h | {cap.vigencia_meses} meses</span>
                  {cap.obligatoria && <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-600 rounded font-medium">Obligatoria</span>}
                  {cap.calendarizable && <span className="text-[10px] px-1.5 py-0.5 bg-teal-100 text-teal-600 rounded font-medium flex items-center gap-0.5"><Calendar className="w-2.5 h-2.5" />Calendarizable</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setCursoPrueba(cap)}
                className="text-xs font-semibold px-2.5 py-1.5 bg-teal-600 text-white rounded hover:bg-teal-700 inline-flex items-center gap-1"
                data-testid={`prueba-cap-${cap.id}`}
                title="Configurar prueba">
                <ClipboardList className="w-3.5 h-3.5" /> Configurar Prueba
              </button>
              <button onClick={() => openEdit(cap)} className="p-2 hover:bg-slate-100 rounded-lg text-slate-500" data-testid={`edit-cap-${cap.id}`}>
                <Pencil className="w-4 h-4" />
              </button>
              <button onClick={() => handleDelete(cap.id, cap.nombre)} className="p-2 hover:bg-red-50 rounded-lg text-red-500" data-testid={`delete-cap-${cap.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
        {capacitaciones.length === 0 && (
          <div className="text-center py-12 text-slate-400">
            <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No hay capacitaciones registradas</p>
          </div>
        )}
      </div>

      {capacitaciones.length > PAGE_SIZE && (() => {
        const totalPages = Math.max(1, Math.ceil(capacitaciones.length / PAGE_SIZE));
        const currentPage = Math.min(page, totalPages);
        return (
          <div className="flex items-center justify-between mt-1 px-1" data-testid="paginacion-cursos">
            <p className="text-[11px] text-slate-500">
              Mostrando <strong>{(currentPage - 1) * PAGE_SIZE + 1}</strong>–<strong>{Math.min(currentPage * PAGE_SIZE, capacitaciones.length)}</strong> de <strong>{capacitaciones.length}</strong> capacitaciones
            </p>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                data-testid="pag-prev"
                className="px-2.5 py-1 text-xs rounded border border-slate-200 bg-white text-slate-600 hover:border-slate-400 disabled:opacity-40 disabled:cursor-not-allowed">← Anterior</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} onClick={() => setPage(p)}
                  data-testid={`pag-page-${p}`}
                  className={`min-w-[28px] px-2 py-1 text-xs rounded border transition-colors ${p === currentPage ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}>{p}</button>
              ))}
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                data-testid="pag-next"
                className="px-2.5 py-1 text-xs rounded border border-slate-200 bg-white text-slate-600 hover:border-slate-400 disabled:opacity-40 disabled:cursor-not-allowed">Siguiente →</button>
            </div>
          </div>
        );
      })()}

      {/* Modal Form */}
      {showForm && createPortal(
        <div style={{backgroundColor:"rgba(0,0,0,0.5)"}} className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => { setShowForm(false); resetForm(); }}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
              <h2 className="text-lg font-bold text-slate-900">{editing ? 'Editar' : 'Nueva'} Capacitacion</h2>
              <button onClick={() => { setShowForm(false); resetForm(); }} className="p-1.5 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <Label>Nombre</Label>
                <input type="text" value={form.nombre} onChange={e => setForm({...form, nombre: e.target.value})} required
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mt-1" />
              </div>
              <div>
                <Label>Descripcion</Label>
                <textarea value={form.descripcion} onChange={e => setForm({...form, descripcion: e.target.value})} rows={2}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mt-1 resize-none" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Vigencia (meses)</Label>
                  <input type="number" value={form.vigencia_meses} onChange={e => setForm({...form, vigencia_meses: parseInt(e.target.value)||12})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mt-1" />
                </div>
                <div>
                  <Label>Horas</Label>
                  <input type="number" value={form.horas} onChange={e => setForm({...form, horas: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mt-1" />
                </div>
                <div>
                  <Label>Modalidad</Label>
                  <select value={form.modalidad} onChange={e => setForm({...form, modalidad: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mt-1">
                    <option value="e-learning">E-Learning</option>
                    <option value="presencial">Presencial</option>
                    <option value="mixta">Mixta</option>
                  </select>
                </div>
              </div>
              <div className="space-y-3 border-t pt-4">
                <div className="flex items-center justify-between">
                  <Label>Obligatoria</Label>
                  <Switch checked={form.obligatoria} onCheckedChange={c => setForm({...form, obligatoria: c})} />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Calendarizable</Label>
                    <p className="text-xs text-slate-400 mt-0.5">Se puede agendar en fechas especificas</p>
                  </div>
                  <Switch checked={form.calendarizable} onCheckedChange={c => setForm({...form, calendarizable: c})} />
                </div>
              </div>
              <button type="submit" className="w-full py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm font-medium">
                {editing ? 'Guardar Cambios' : 'Crear Capacitacion'}
              </button>
            </form>
          </div>
        </div>
      , document.body)}

      {/* Modal Confirmacion */}
      {confirmAction && createPortal(
        <div style={{backgroundColor:"rgba(0,0,0,0.5)"}} className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <p className="text-sm text-slate-700">{confirmAction.message}</p>
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setConfirmAction(null)}
                className="px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200">Cancelar</button>
              <button onClick={confirmAction.onConfirm}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700">Confirmar</button>
            </div>
          </div>
        </div>
      , document.body)}

      {cursoPrueba && <ConfigurarPruebaModal
        curso={cursoPrueba}
        token={token}
        onClose={() => setCursoPrueba(null)}
      />}
    </div>
  );
}
